import React, { useState, useEffect } from "react";
import { Box, IconButton, useTheme, useMediaQuery } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const CustomSlider = ({
  children,
  slidesPerView = { xs: 1, sm: 2, md: 3 },
}: {
  children: React.ReactNode;
  slidesPerView?: number | { xs: number; sm: number; md: number };
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const getCurrentSlidesPerView = () => {
    if (typeof slidesPerView === "number") {
      return slidesPerView;
    }

    if (isMobile) {
      return slidesPerView.xs;
    } else if (isTablet) {
      return slidesPerView.sm;
    } else {
      return slidesPerView.md;
    }
  };

  const currentSlidesPerView = getCurrentSlidesPerView();

  const childrenArray = React.Children.toArray(children);
  const groupedSlides = [];
  for (let i = 0; i < childrenArray.length; i += currentSlidesPerView) {
    groupedSlides.push(childrenArray.slice(i, i + currentSlidesPerView));
  }
  const slidesCount = groupedSlides.length;

  useEffect(() => {
    setActiveStep(0);
  }, [currentSlidesPerView]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) =>
      prevActiveStep === slidesCount - 1 ? 0 : prevActiveStep + 1
    );
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) =>
      prevActiveStep === 0 ? slidesCount - 1 : prevActiveStep - 1
    );
  };

  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          overflow: "hidden",
          gap: "16px",
          pointerEvents: "auto",
        }}
      >
        {groupedSlides.length > 0 ? (
          groupedSlides[activeStep].map((slide, index) => (
            <Box
              key={index}
              sx={{
                flex: `0 0 calc(${100 / currentSlidesPerView}% - ${
                  (16 * (currentSlidesPerView - 1)) / currentSlidesPerView
                }px)`,
                minWidth: 0,
                pointerEvents: "auto",
                "& > *": {
                  pointerEvents: "auto",
                },
              }}
            >
              {slide}
            </Box>
          ))
        ) : (
          <Box sx={{ textAlign: "center", p: 4 }}>
            Please add child elements to display in the slider
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        {groupedSlides.map((_, index) => (
          <Box
            key={index}
            sx={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              mx: 0.5,
              backgroundColor:
                index === activeStep ? "primary.main" : "grey.300",
              cursor: "pointer",
            }}
            onClick={() => setActiveStep(index)}
          />
        ))}
      </Box>

      {groupedSlides.length > 1 && (
        <>
          <IconButton
            onClick={handleBack}
            sx={{
              position: "absolute",
              left: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.9)",
              },
              zIndex: 2,
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <IconButton
            onClick={handleNext}
            sx={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.9)",
              },
              zIndex: 2,
            }}
          >
            <ArrowForwardIcon />
          </IconButton>
        </>
      )}
    </Box>
  );
};

export default CustomSlider;
